import moment from "moment";
import Swal from "sweetalert2";
import { useAppSelector } from "../../redux/store";
import { Projet } from "../../utils/api/launchCampagn/projet.type";
import {
  useRetraitsQuery,
  useUpdateStatutMutation,
} from "../../utils/api/retrait/retrait.api";
import {
  destinationRetrait,
  origineVersement,
  Retrait,
  RETRAIT_STATUT,
  RetraitStatut,
  transitionsAdmin,
} from "../../utils/api/retrait/retrait.type";
import { COLORS } from "../../utils/constants";
import { formatMontant } from "../../utils/utils";
import "./InvestissementModal.css";


// Ces boutons ne s'affichent que sur le circuit manuel : l'admin paie
// lui-meme, puis consigne ce qu'il a fait. Les libelles le disent.
const ACTIONS: Record<string, string> = {
  approuve: "Approuver",
  refuse: "Refuser",
  en_cours: "Paiement en cours",
  verse: "Marquer comme versé",
  echoue: "Marquer en échec",
};

// Suivi et historique des demandes de retrait d'un projet.
// Le meme ecran sert au porteur (lecture seule) et a l'admin (actions) :
// c'est le backend qui refuse les transitions, ce composant ne fait que
// proposer celles qui sont possibles.
function RetraitsModal({
  modalId,
  projet,
}: {
  modalId: string;
  projet: Projet;
}) {
  const user = useAppSelector((s) => s.user.user);
  const isAdmin = user?.user_type === "admin";
  const { data: retraits = [], isLoading } = useRetraitsQuery({
    projet: projet?.id,
  });
  const [updateStatut, { isLoading: isUpdating }] = useUpdateStatutMutation();

  const changer = async (retrait: Retrait, statut: RetraitStatut) => {
    let motif: string | undefined;
    if (statut === "refuse" || statut === "echoue") {
      const { value, isDismissed } = await Swal.fire({
        title: ACTIONS[statut],
        input: "textarea",
        inputLabel: "Motif (communiqué au porteur du projet)",
        inputPlaceholder: "Expliquez la raison…",
        showCancelButton: true,
        cancelButtonText: "Annuler",
        confirmButtonText: "Valider",
        confirmButtonColor: COLORS.green,
      });
      if (isDismissed) return;
      motif = value || undefined;
    }
    const res: any = await updateStatut({ slug: retrait.slug, statut, motif });
    if (res?.error) {
      Swal.fire({
        icon: "error",
        title: "Action impossible",
        text: res.error?.data?.message || "Veuillez réessayer.",
        confirmButtonColor: COLORS.green,
      });
    }
  };

  return (
    <div
      className="modal fade"
      id={modalId}
      data-bs-backdrop="static"
      data-backdrop="false"
      data-bs-keyboard="false"
      aria-labelledby={`label${modalId}`}
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content custom-modal-investissement">
          <div className="modal-header modal-header-investissement">
            <h5 className="modal-title" id={`label${modalId}`}>
              Retraits — {projet?.name}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Fermer"
            ></button>
          </div>
          <div className="modal-body modal-invessetissement-body">
            <div className="modal-investissement-montants mb-3">
              <span>
                Collecté : <b>{formatMontant(projet?.funds)}</b>
              </span>
              <span>
                Disponible : <b>{formatMontant(projet?.montant_total_restant)}</b>
              </span>
              <span>
                Versé : <b>{formatMontant(projet?.montant_total_debit)}</b>
              </span>
            </div>

            {isLoading && <p>Chargement…</p>}
            {!isLoading && retraits.length === 0 && (
              <p className="modal-investissement-description">
                Aucune demande de retrait pour ce projet.
              </p>
            )}

            {retraits.length > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Montant</th>
                    <th>Reçu</th>
                    <th>Destination</th>
                    <th>Statut</th>
                    <th>Versement</th>
                    <th>Motif</th>
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {retraits.map((r) => {
                    const st = RETRAIT_STATUT[r.statut];
                    return (
                      <tr key={r.slug}>
                        <td>{moment(r.created_at).format("DD/MM/YYYY")}</td>
                        <td>{formatMontant(r.montant)}</td>
                        <td>{formatMontant(r.montant_net)}</td>
                        <td style={{ fontSize: 12 }}>{destinationRetrait(r)}</td>
                        <td>
                          <span style={{ color: st?.color, fontWeight: 600 }}>
                            {st?.label || r.statut}
                          </span>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {(() => {
                            const o = origineVersement(r);
                            if (!o) return "—";
                            return (
                              <>
                                <span style={{ fontWeight: 600, color: o.auto ? "#0FBF56" : "#6c757d" }}>
                                  {o.label}
                                </span>
                                <div style={{ fontSize: 11, color: "rgba(0,0,0,0.5)" }}>
                                  {o.reference}
                                </div>
                              </>
                            );
                          })()}
                        </td>
                        <td style={{ maxWidth: 180 }}>{r.motif || "—"}</td>
                        {isAdmin && (
                          <td>
                            {transitionsAdmin(r).map((s) => (
                              <button
                                key={s}
                                type="button"
                                disabled={isUpdating}
                                className="btn btn-sm me-1 mb-1"
                                style={{
                                  border: `1px solid ${RETRAIT_STATUT[s].color}`,
                                  color: RETRAIT_STATUT[s].color,
                                  fontSize: 11,
                                }}
                                onClick={() => changer(r, s)}
                              >
                                {ACTIONS[s]}
                              </button>
                            ))}
                            {transitionsAdmin(r).length ===
                              0 && "—"}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RetraitsModal;
